import type { components } from "../../../api/generated";
import type {
  EnterFulfillmentWorkspaceResult,
  FulfillmentSupplierOption,
  FulfillmentResourceMentionCandidate,
  FulfillmentTransportResourceCandidate,
  FulfillmentTransportPoint,
} from "../types";

export type EnterResponseContract =
  components["schemas"]["CourseRouteResponse_EnterCourseRouteWorkspaceDTO_"];
export type SupplierOptionsResponseContract =
  components["schemas"]["CourseRouteResponse_list_CourseRouteSupplierOptionDTO__"];
export type TransportPointsResponseContract =
  components["schemas"]["CourseRouteResponse_list_CourseRouteTransportPointDTO__"];
export type ResourceCandidatesResponseContract =
  components["schemas"]["CourseRouteResponse_list_CourseRouteResourceCandidateDTO__"];
export type TransportResourceCandidatesResponseContract =
  components["schemas"]["CourseRouteResponse_list_CourseRouteTransportResourceCandidateDTO__"];

const SUPPLIER_SUBJECT_NATURES = new Set<NonNullable<
  FulfillmentSupplierOption["subjectNature"]
>>(["individual", "organization", "internal", "unknown"]);

/** 映射按课程线路幂等进入结果。 */
export function mapEnterFulfillmentWorkspace(
  raw: EnterResponseContract["data"],
): EnterFulfillmentWorkspaceResult {
  return { workspaceId: raw.workspace_id, created: raw.created };
}

/** 映射供应商主数据候选。 */
export function mapSupplierOptions(
  raw: SupplierOptionsResponseContract["data"],
): FulfillmentSupplierOption[] {
  return raw.map((item) => ({
    id: item.supplier_id,
    code: item.supplier_code,
    name: item.supplier_name,
    shortName: item.supplier_short_name ?? null,
    type: item.supplier_type,
    subjectNature: SUPPLIER_SUBJECT_NATURES.has(
      item.subject_nature as NonNullable<FulfillmentSupplierOption["subjectNature"]>,
    )
      ? item.subject_nature as NonNullable<FulfillmentSupplierOption["subjectNature"]>
      : null,
    companySubjectName: item.company_subject_name ?? null,
    canBeSettlementSubject: item.can_be_settlement_subject,
    canBeFulfillmentSubject: item.can_be_fulfillment_subject,
    contactName: item.contact_name ?? null,
    contactPhone: item.contact_phone ?? null,
  }));
}

/** 映射机场与火车站主数据候选。 */
export function mapFulfillmentTransportPoints(
  raw: TransportPointsResponseContract["data"],
): FulfillmentTransportPoint[] {
  return raw.map((point) => ({
    id: point.point_id,
    name: point.name,
    shortName: point.short_name ?? null,
    pointType: point.point_type,
    city: point.city ?? null,
  }));
}

/** 映射确认件住宿、包车、考察点与大交通的资源或名称候选。 */
export function mapFulfillmentResourceCandidates(
  raw: ResourceCandidatesResponseContract["data"],
): FulfillmentResourceMentionCandidate[] {
  const candidates = raw.map((item) => ({
    resourceType: item.resource_type,
    level: item.level,
    mentionText: item.mention_text,
    spuTableName: item.spu_table_name ?? null,
    spuId: item.spu_id ?? null,
    spuName: item.spu_name ?? null,
    skuTableName: item.sku_table_name ?? null,
    skuId: item.sku_id ?? null,
    skuName: item.sku_name ?? null,
    description: item.description ?? null,
    transportMode: item.transport_mode ?? null,
    travelerRole: item.traveler_role ?? null,
    ...(item.vehicle_spec_name ? { vehicleSpecName: item.vehicle_spec_name } : {}),
    ...(item.room_capacity != null ? { roomCapacity: item.room_capacity } : {}),
    ...(item.reference_unit_price != null ? { referenceUnitPrice: item.reference_unit_price } : {}),
    ...(item.reference_price_date ? { referencePriceDate: item.reference_price_date } : {}),
    ...(item.reference_price_type ? { referencePriceType: item.reference_price_type } : {}),
    ...(item.reference_price_currency ? { referencePriceCurrency: item.reference_price_currency } : {}),
  }));
  return [...new Map(candidates.map((candidate) => {
    const key = candidate.resourceType === "住宿" && candidate.level === "sku"
      ? `stay:${candidate.skuName ?? candidate.mentionText}`
      : candidate.resourceType === "包车"
        ? `bus:${candidate.vehicleSpecName ?? candidate.skuName ?? candidate.mentionText}`
        : [candidate.resourceType, candidate.level, candidate.spuId, candidate.skuId].join(":");
    return [key, candidate] as const;
  })).values()];
}

/** 映射按起终点精确解析的大交通资源候选。 */
export function mapFulfillmentTransportResourceCandidates(
  raw: TransportResourceCandidatesResponseContract["data"],
): FulfillmentTransportResourceCandidate[] {
  return raw.map((item) => ({
    transportMode: item.transport_mode,
    fromPointId: item.from_point_id,
    fromPointName: item.from_point_name,
    toPointId: item.to_point_id,
    toPointName: item.to_point_name,
    seatClass: item.seat_class ?? null,
    spuTableName: item.spu_table_name,
    spuId: item.spu_id,
    spuName: item.spu_name,
    skuTableName: item.sku_table_name,
    skuId: item.sku_id,
    skuName: item.sku_name,
    travelerRole: null,
    description: item.description ?? null,
  }));
}
